import axios from 'axios';
import { useState, useEffect } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import { TFormValues } from '../../types';
import { useTranslation } from 'react-i18next';

interface IProps {
    storeField: string;
    storeEmailField?: string;
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    setFieldValue(field: keyof TFormValues, value: string): void;
}

interface IStoreOption {
    email: string;
    name: string;
    storenumber: string;
}

interface IJsonStore {
    u_smd_address: string;
    u_smd_address_fr_be: string;
    u_smd_addressnumber: string;
    u_smd_city: string;
    u_smd_city_fr_be: string;
    u_smd_emailstore: string;
    u_smd_storenumber: string;
}

export const StoreSection = ({
    storeField,
    storeEmailField,
    getFieldError,
    setFieldValue,
}: IProps) => {
    const [stores, setStores] = useState<IStoreOption[] | undefined>();
    const {
        t,
        i18n: { language },
    } = useTranslation();

    useEffect(() => {
        if (!process.env.REACT_APP_STORES_URL_PBE) return;

        axios
            .get<IJsonStore[]>(process.env.REACT_APP_STORES_URL_PBE)
            .then(({ data }) => {
                const allStores: IStoreOption[] = data.map((store) => ({
                    name:
                        language === 'fr'
                            ? `${store.u_smd_city_fr_be} - ${store.u_smd_address_fr_be} ${store.u_smd_addressnumber}`
                            : `${store.u_smd_city} - ${store.u_smd_address} ${store.u_smd_addressnumber}`,
                    storenumber: store.u_smd_storenumber,
                    email: store.u_smd_emailstore,
                }));

                setStores(allStores);
            })
            .catch(() => console.error('Error fetching store list'));
    }, [language]);

    const handleStoreChange = (
        _e: React.SyntheticEvent<Element, Event>,
        value: IStoreOption | null
    ) => {
        if (!value) return;

        if (storeEmailField) {
            setFieldValue(storeEmailField as keyof TFormValues, value.email);
        }

        setFieldValue(storeField as keyof TFormValues, value.name);
    };

    return (
        <>
            {stores && (
                <Autocomplete
                    getOptionLabel={(option: IStoreOption) => option.name}
                    id="store-select"
                    options={stores}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={
                                !!getFieldError(storeField as keyof TFormValues)
                            }
                            helperText={getFieldError(
                                storeField as keyof TFormValues
                            )}
                            label={t('chooseStore')}
                        />
                    )}
                    onChange={handleStoreChange}
                />
            )}
        </>
    );
};
